import React, { useEffect, useState } from 'react';
import {
  TextField,
  FormHelperText,
  IconButton,
  Box,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export const GithubUserSelector = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<string[]>) => {
  const catalogApi = useApi(catalogApiRef);
  const [users, setUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState<string[]>(formData || []);
  const [selectedUser, setSelectedUser] = useState<string>('');

  const title = schema.title || 'GitHub Users';

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Get all User entities from catalog
        const { items: userEntities } = await catalogApi.getEntities({
          filter: { kind: 'User', 'metadata.tags': 'github-org' },
        });

        // Extract usernames from metadata.name
        const usernames = userEntities
          .map(user => user.metadata.name)
          .filter(Boolean)
          .sort();

        setUsers(usernames);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [catalogApi]);

  useEffect(() => {
    onChange(selectedUsers);
  }, [selectedUsers, onChange]);

  const handleAddUser = () => {
    if (selectedUser && !selectedUsers.includes(selectedUser)) {
      const newUsers = [...selectedUsers, selectedUser];
      setSelectedUsers(newUsers);
      setSelectedUser('');
    }
  };

  const handleRemoveUser = (username: string) => {
    const newUsers = selectedUsers.filter(u => u !== username);
    setSelectedUsers(newUsers);
  };

  return (
    <Box>
      <Typography
        variant="subtitle1"
        style={{ marginTop: 16, marginBottom: 8 }}
      >
        {title}
      </Typography>
      <Box display="flex" alignItems="flex-start" style={{ gap: 16 }}>
        <Box flex={1}>
          <Autocomplete
            options={users.filter(u => !selectedUsers.includes(u))}
            value={selectedUser || null}
            onChange={(_, newValue) => setSelectedUser(newValue || '')}
            disabled={loading}
            renderInput={params => (
              <TextField
                {...params}
                label="Select Github Username"
                margin="normal"
                fullWidth
                helperText={
                  loading
                    ? 'Loading users...'
                    : users.length === 0
                    ? 'No users found in catalog'
                    : 'Select a user to add'
                }
              />
            )}
          />
        </Box>
        <Box marginTop={2}>
          <IconButton
            color="primary"
            onClick={handleAddUser}
            disabled={!selectedUser}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      {selectedUsers.length > 0 && (
        <Box marginTop={2}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Users ({selectedUsers.length})
          </Typography>
          {selectedUsers.map(username => (
            <Box
              key={username}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom={1}
              padding={1}
              border="1px solid #e0e0e0"
              borderRadius={4}
            >
              <Typography>{username}</Typography>
              <IconButton
                size="small"
                onClick={() => handleRemoveUser(username)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {rawErrors?.length > 0 && required && selectedUsers.length === 0 && (
        <FormHelperText error>At least one user is required</FormHelperText>
      )}
    </Box>
  );
};
